import { useMemo } from 'react';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';

import { useMediaAssetsBasePath } from 'src/app/shared/hooks/assets';
import { useCountryCode } from 'src/app/shared/hooks/l10n';
import { useTypesenseConfig } from 'src/app/shared/hooks/typesense';

import { DECISION_MAKERS_PRESET, searchDecisionMakersTypesense } from './api/searchDecisionMakersTypesense';
import type { DecisionMaker } from './types';

type Result =
	| { readonly status: 'loading' }
	| { readonly status: 'error' }
	| { readonly status: 'loaded'; readonly data: readonly DecisionMaker[] };

export function useDecisionMakerSearchTypesense(input: string): Result {
	const countryCode = useCountryCode();
	const { config } = useTypesenseConfig();
	const imageHost = `${useMediaAssetsBasePath({ mode: 'view' })}/`;

	const getTypesenseClient = useMemo(
		() => () =>
			new TypesenseInstantsearchAdapter({
				server: config,
				additionalSearchParameters: { preset: DECISION_MAKERS_PRESET },
			}).typesenseClient,
		[config],
	);

	const { data, isPending, isError } = useQuery({
		queryKey: ['typesense-decision-makers', input, countryCode, imageHost, config],
		queryFn: async () =>
			searchDecisionMakersTypesense({ searchQuery: input, countryCode, imageHost }, getTypesenseClient()),
		placeholderData: keepPreviousData,
	});

	if (isPending) return { status: 'loading' };
	if (isError) return { status: 'error' };
	return { status: 'loaded', data };
}
